import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useMemo, useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import { generatePath } from 'react-router-dom';

import { FilterInDropdown } from '@sorare/core/src/atoms/dropdowns/FilterInDropdown';
import { RadioGroup } from '@sorare/core/src/atoms/inputs/RadioGroup';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { Standings as DumbStandings } from '@sorare/core/src/components/seo/Team/Standings';
import { FOOTBALL_CLUBS_SLUG } from '@sorare/core/src/constants/__generated__/routes';
import { Warning } from '@sorare/core/src/contexts/intl/Warning';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { playerDetails } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import { format as formatSeason } from '@sorare/core/src/lib/seasons';

import type {
  FootballSeasonStandingsQuery,
  FootballSeasonStandingsQueryVariables,
} from './__generated__/index.graphql';

const messages = defineMessages({
  noSeasons: {
    id: 'seo.Team.Standings.noSeasons',
    defaultMessage: 'No standings for {displayName}',
  },
});

const FOOTBALL_SEASON_STANDINGS_QUERY = gql`
  query FootballSeasonStandingsQuery($slug: String!, $seasonStartYear: Int!) {
    football {
      competition(slug: $slug) {
        slug
        displayName
        pictureUrl
        released
        stages(seasonStartYear: $seasonStartYear) {
          id
          name
          groups {
            id
            name
            contestants {
              id
              rank
              rankStatus
              team {
                slug
                name
                pictureSecondaryUrl
              }
              ... on FootballContestantType {
                id
                matchesDrawn
                matchesLost
                matchesPlayed
                matchesWon
                goalsFor
                goalsAgainst
                points
              }
            }
          }
        }
      }
    }
  }
` as TypedDocumentNode<
  FootballSeasonStandingsQuery,
  FootballSeasonStandingsQueryVariables
>;

type Props = {
  displayName: string;
  as?: keyof HTMLElementTagNameMap;
  clubSlug?: string;
  competitionSlug: string;
  seasons: { startYear: number }[];
};

export const SeasonStandings = ({
  displayName,
  as,
  clubSlug,
  competitionSlug,
  seasons,
}: Props) => {
  const [season, setSeason] = useState(seasons.slice(-1)[0]);
  const { data, loading } = useQuery(FOOTBALL_SEASON_STANDINGS_QUERY, {
    variables: { slug: competitionSlug, seasonStartYear: season?.startYear },
    skip: !seasons.length,
  });

  const options = useMemo(
    () =>
      seasons.map(s => ({
        label: formatSeason(s.startYear),
        value: s.startYear.toString(),
      })),
    [seasons]
  );

  if (!seasons.length) {
    return (
      <FullWidthContainer>
        <Warning
          title={messages.noSeasons}
          titleValues={{ displayName }}
          variant="yellow"
        />
      </FullWidthContainer>
    );
  }

  if (loading) return <LoadingIndicator fullHeight />;
  if (!data) return null;

  const { competition } = data.football;

  return (
    <DumbStandings
      displayName={displayName}
      as={as}
      filters={
        <div>
          <FilterInDropdown buttonLabel={formatSeason(season.startYear)}>
            {({ closeDropdown }) => (
              <RadioGroup
                modalSelect
                options={options}
                initiallySelectedValue={season.startYear.toString()}
                name="club-standings-season"
                onChange={value => {
                  const selectedSeason = seasons.find(
                    s => s.startYear.toString() === value
                  );
                  if (selectedSeason) {
                    setSeason(selectedSeason);
                    closeDropdown();
                  }
                }}
              />
            )}
          </FilterInDropdown>
        </div>
      }
      stats={['played', 'won', 'drawn', 'lost', 'gf', 'ga', 'gd', 'points']}
      widths={{
        played: 5,
        won: 5,
        drawn: 5,
        lost: 5,
        gf: 5,
        ga: 5,
        gd: 5,
        points: 5,
      }}
      headers={{
        played: <FormattedMessage {...playerDetails.played} />,
        won: <FormattedMessage {...playerDetails.won} />,
        drawn: <FormattedMessage {...playerDetails.drawn} />,
        lost: <FormattedMessage {...playerDetails.lost} />,
        gf: <FormattedMessage {...playerDetails.goalsFor} />,
        ga: <FormattedMessage {...playerDetails.goalsAgainst} />,
        gd: <FormattedMessage {...playerDetails.goalsDiff} />,
        points: <FormattedMessage {...playerDetails.points} />,
      }}
      groups={competition.stages.flatMap(stage => {
        return stage.groups.map(group => ({
          title: stage.groups.length > 1 ? group.name : null,
          standings: group.contestants
            .map(contestant => {
              if (!isType(contestant, 'FootballContestantType')) return null;
              return {
                slug: contestant.team!.slug,
                name: contestant.team!.name,
                pictureUrl: contestant.team!.pictureSecondaryUrl,
                to: generatePath(FOOTBALL_CLUBS_SLUG, {
                  slug: contestant.team!.slug,
                }),
                rank: contestant.rank || 0,
                rankStatus: contestant.rankStatus,
                stats: {
                  played: contestant.matchesPlayed,
                  won: contestant.matchesWon,
                  drawn: contestant.matchesDrawn,
                  lost: contestant.matchesLost,
                  gf: contestant.goalsFor || 0,
                  ga: contestant.goalsAgainst || 0,
                  gd:
                    (contestant.goalsFor || 0) - (contestant.goalsAgainst || 0),
                  points: contestant.points,
                },
                highlightedSlug: clubSlug,
              };
            })
            .filter(Boolean),
        }));
      })}
    />
  );
};
